import type { CSSProperties, ReactNode, RefObject, SVGProps } from "react";
import { useEffect, useRef } from "react";

type RuntimePhoneFrameAttributes = Pick<SVGProps<SVGSVGElement>, "fill" | "viewBox" | "xmlns">;

interface RuntimePhoneFrameGeometryAttributes {
  contentAttributes: { style: CSSProperties };
  frameAttributes: RuntimePhoneFrameAttributes;
}

interface RuntimePhoneFrameGeometryProps {
  containerRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  frameRef: RefObject<SVGSVGElement | null>;
  children: (attributes: RuntimePhoneFrameGeometryAttributes) => ReactNode;
}

const contentStyle = {
  width: 288,
  height: 606,
  padding: "10px 15px 14px 16px",
  borderRadius: 60,
  transformOrigin: "top left",
} satisfies CSSProperties;

const frameAttributes = {
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  viewBox: "0 0 765 1544",
} satisfies RuntimePhoneFrameAttributes;

/** Keeps the borrowed phone frame and its scaled HTML content in the same coordinate system. */
export function RuntimePhoneFrameGeometry({
  containerRef,
  contentRef,
  frameRef,
  children,
}: RuntimePhoneFrameGeometryProps): ReactNode {
  const observerRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    observerRef.current ??= new ResizeObserver(() => {
      if (contentRef.current && frameRef.current) {
        const frameBounds = frameRef.current.getBoundingClientRect();
        const scaleX = frameBounds.width / contentRef.current.offsetWidth;
        const scaleY = frameBounds.height / contentRef.current.offsetHeight;

        contentRef.current.style.transform = `scale(${scaleX}, ${scaleY})`;
        frameRef.current.style.visibility = "visible";
      }
    });

    const observer = observerRef.current;
    const container = containerRef.current;
    if (container) {
      observer.observe(container);

      return () => {
        observer.unobserve(container);
      };
    }
  }, [containerRef, contentRef, frameRef]);

  return children({ contentAttributes: { style: contentStyle }, frameAttributes });
}
